import React from "react";

import SkeletonBlock from "../common/SkeletonBlock";

/**
 * Shimmer placeholder for the single-result page while the measurement document
 * is being fetched. Mirrors the result view layout (left-aligned header with a
 * mode icon, then a couple of summary sections) so the page fades in without a
 * layout shift.
 *
 * Note: this is the "loading" state. Once the document exists but its results
 * are still being computed, the page shows the animated lung loader instead —
 * the shimmer is for loading, the lung is for "results are being calculated".
 */
function SummarySectionSkeleton({ rows }) {
  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SkeletonBlock className="h-8 w-8 rounded-lg shrink-0" />
        <SkeletonBlock className="h-5 w-40 max-w-[55%] rounded-full" />
      </div>
      <div className="flex flex-col gap-2">
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="flex items-center justify-between gap-3"
          >
            <SkeletonBlock className="h-4 w-28 rounded-full" />
            <SkeletonBlock className="h-4 w-14 rounded-full shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ResultPageLoadingShell() {
  return (
    <div
      className="w-full max-w-lg h-full min-h-0 overflow-y-auto"
      data-testid="result-page-loading-shell"
      role="status"
      aria-live="polite"
    >
      <section className="min-h-full p-4 pt-0 sm:pt-4 flex flex-col items-stretch gap-6">
        <div className="flex flex-col w-full items-start gap-2">
          <div className="flex items-center gap-2">
            <SkeletonBlock className="h-6 w-6 rounded-lg shrink-0" />
            <SkeletonBlock className="h-6 w-40 rounded-full" />
          </div>
          <SkeletonBlock className="h-3 w-28 rounded-full" />
        </div>

        <SummarySectionSkeleton rows={2} />
        <SummarySectionSkeleton rows={4} />
      </section>
    </div>
  );
}
